/* global QalaDynamicPricingMatrixCombinations */
import APIHandler from './ApiHandler';

/**
 * Main class handling Single product price logic in JS.
 *
 * TODO: We could probably clean this up by moving logic into other files.
 * A FieldUtils class for example.
 */
export default class PDPPricingManager {
	constructor() {
		// Bail right away if there is no config for the product.
		if ( typeof QalaDynamicPricingMatrixCombinations === 'undefined' || QalaDynamicPricingMatrixCombinations?.config === null ) {
			return;
		}

		this.apiHandler = new APIHandler();
		this.pricingData = QalaDynamicPricingMatrixCombinations?.config?.pricing;
		this.settings = QalaDynamicPricingMatrixCombinations?.config?.settings;
		this.selectedOptions = {};
		this.productId = QalaDynamicPricingMatrixCombinations?.product_id;
		this.pricingRuleId =
			QalaDynamicPricingMatrixCombinations?.config?.pricing_rule_id;
		this.inputTarget = '.qdp-matrix-input';
		this.addToCartButton = document.querySelector( 'form.cart button[type="submit"]' );
		this.resetButton = document.querySelector( '.qdp__reset-button' ) ?? null;
		this.priceElement = document.querySelector( '.qdp__price' );
		this.inputType = 'select';

		this.setInitialconfig();
	}

	/**
	 * Sets the initial configuration of applied values if there are any and adds the eventlisteners to each field.
	 *
	 * @return {void}
	 */
	setInitialconfig = () => {
		// Set up a listener on the add to cart button.
		this.addToCartButton?.addEventListener( 'click', event => this.handleAddToCartClick( event ) );

		// Set up a listener on the reset button if it exists.
		if ( this.resetButton ) {
			this.resetButton?.addEventListener( 'click', this.resetFormFields );
		}

		// Loop through our settings to configure each form field.
		if ( this.settings ) {
			Object.keys( this.settings ).forEach( settingId => {
				const setting = this.settings[ settingId ];
				const input = document.querySelector( this.inputTarget + '--' + settingId );
				if ( ! input ) {
					return;
				}

				this.setInitialValues( settingId, input );

				// If all values have been set initially we actually want to set options and prices too.
				// We might for example be able to populate the settings with GET parameters at some point, or set default values.
				if ( this.areAllOptionsSelected() ) {
					this.updateOptionsAndPrice();
				} else {
					// If not all configs are set, we disable the button and hide the price.
					this.disableAddToCart( true );
					this.removePrice();
				}

				// Listen to changes to any input that isn't hidden.
				if ( setting.type !== 'hidden' ) {
					this.initEventListener( settingId, input );
				}
			} );
		}
	};

	/**
	 * Sets up an individual change event listener on a field.
	 *
	 * @param {string}      settingId
	 * @param {HTMLElement} initElement
	 * @return {void}
	 */
	initEventListener = ( settingId, initElement ) => {
		const inputType = initElement.tagName.toLowerCase();
		const inputSubType = initElement.type ?? '';
		let eventListenerType = 'change';
		let validationEventListener = false;

		// Set up different event listener depending on input type.
		switch ( inputType ) {
			case 'input':
				eventListenerType = 'blur';
				validationEventListener = ( inputSubType === 'number' );
				break;
			case 'select':
			default:
				eventListenerType = 'change';
		}

		initElement.addEventListener( eventListenerType, event =>
			this.handleInputChange( settingId, event, inputType )
		);

		if ( validationEventListener ) {
			initElement.addEventListener( 'input', event =>
				this.validateNumberInput( event )
			);
		}
	};

	/**
	 * Sets the start value for a given element.
	 *
	 * @param {string}      settingId
	 * @param {HTMLElement} initElement
	 */
	setInitialValues = ( settingId, initElement ) => {
		if ( ! initElement?.value ) {
			return;
		}

		this.selectedOptions[ settingId ] = initElement.value;
	};

	/**
	 * Handles the state of the add to cart when it is clicked.
	 *
	 * @param {Object} event the event object from a click event.
	 */
	handleAddToCartClick = event => {
		// If the button should be disabled we stop the submission.
		if ( this.addToCartButton?.classList.contains( 'disabled' ) ) {
			event.preventDefault();
		}

		if ( this.addToCartButton?.classList.contains( 'qdp-requires-config' ) ) {
			this.notifyMissingConfig();
		}
	};

	/**
	 * Notify the customer that they need to have a proper config before they can purchase the product.
	 */
	notifyMissingConfig = () => {
		// We only do this if there is a message in the settings.
		const message = QalaDynamicPricing?.settings?.text_disabled_add_to_cart;

		if ( message ) {
			window.alert( message ); // eslint-disable-line no-alert
		}
	};

	/**
	 * Enforce min/max values on number inputs, supporting decimal values and incomplete decimal entries.
	 *
	 * @param {Event}   event    The event triggered by the input change.
	 * @param {boolean} minCheck Flag to decide if minimum value check is necessary.
	 */
	validateNumberInput = ( event, minCheck = false ) => {
		const numberInput = event.target;
		const inputValue = numberInput.value;

		// Only continue processing if the value is complete (no unfinished decimals)
		if ( ! inputValue || inputValue.match( /^-?\d*\.?\d*$/ ) ) {
			const value = parseFloat( inputValue );

			// Handle cases where parseFloat results in NaN due to incomplete entries like "123."
			if ( isNaN( value ) ) {
				return;
			}

			// Define min and max as floating point numbers, and only update if necessary
			const min = parseFloat( numberInput.min );
			const max = parseFloat( numberInput.max );

			if ( value > max ) {
				numberInput.value = numberInput.max;
			} else if ( minCheck && value < min ) {
				numberInput.value = numberInput.min;
			}
		}
	};

	/**
	 * Handles when a select dropdown changes.
	 * Handles when a select, text, or number element changes.
	 *
	 * @param {string} settingId the Settings ID.
	 * @param {Event}  event     The Event.
	 */
	handleInputChange = async ( settingId, event ) => {
		// Delete the value from our selected options if its empty. 0 does not count as empty as it could be a valid value.
		if ( ! event.target.value && event.target.value !== 0 && event.target.value !== '0' ) {
			delete this.selectedOptions[ settingId ];
		} else {
			const inputSubType = event.target.type ?? '';
			// If the input changed is a number, we want to validate it again but this time also validate the minimum.
			if ( inputSubType === 'number' ) {
				this.validateNumberInput( event, true );
			}

			this.selectedOptions[ settingId ] = event.target.value;
		}

		this.updateOptionsAndPrice();
	};

	/**
	 * Disable or enable the add to cart button.
	 *
	 * @param {boolean} disabledState the disabled state we want to set.
	 */
	disableAddToCart = disabledState => {
		if ( disabledState ) {
			this.addToCartButton?.classList.add( 'disabled', 'qdp-requires-config' );
		} else {
			this.addToCartButton?.classList.remove( 'disabled', 'qdp-requires-config' );
		}
	};

	/**
	 * Remove the price if any is shown.
	 */
	removePrice = () => {
		this.priceElement.innerHTML = '';
	};

	/**
	 * Call the endpoint to get the updated options and possibly price based on current form field selections and inputs.
	 *
	 * @return {void}
	 */
	getMatrixData = async () => {
		try {
			const payload = {
				product_id: this.productId,
				pricing_rule_id: this.pricingRuleId,
				selected_options: this.selectedOptions,
				input_type: this.inputType,
			};

			const response = await this.apiHandler.fetchFromAPI(
				'/frontend/get-matrix-data',
				payload,
				'POST'
			);

			return response;
		} catch ( error ) {
			console.error( 'Error fetching matrix data:', error ); // eslint-disable-line
		}
	};

	/**
	 * Updates the form field options based on the response from the API on which options can be selected with the current selection made so far.
	 *
	 * @return {void}
	 */
	updateOptionsAndPrice = async () => {
		// Disable add to cart while we work.
		this.disableAddToCart( true );
		// If all options are empty we actually want to reset all form fields.
		if ( Object.keys( this.selectedOptions ).length === 0 ) {
			this.disableAddToCart( true );
			this.resetFormFields();
			return;
		}

		const newSettingsAndPrice = await this.getMatrixData();
		// If we had an error or it was aborted it'll be null so we need to check for that and just bail.
		if ( ! newSettingsAndPrice ) {
			return;
		}

		const { settings, price } = newSettingsAndPrice;

		// If price exists and is not empty lets set it!
		if ( typeof price !== 'undefined' && price !== '' ) {
			this.updatePrice( price );

			// This should also mean that we have enough information to let them add the product to cart!
			this.disableAddToCart( false );
		} else {
			// No price means we need to disable button and remove price.
			this.disableAddToCart( true );
			this.removePrice();
		}

		if ( settings ) {
			// Iterate over the response data
			Object.entries( settings ).forEach( ( [ fieldId, options ] ) => {
				const formElement = document.querySelector( this.inputTarget + '--' + fieldId );
				if ( ! formElement ) {
					return;
				}

				const inputType = formElement.tagName.toLowerCase();
				// Run different update functions depending on what type of field we are dealing with.
				switch ( inputType ) {
					case 'input':
						this.updateInputField( formElement, options );
						break;
					case 'select':
						this.updateSelectField( formElement, options );
						break;
					default:
				}

				// Enable the element if it was previously disabled
				formElement.disabled = false;
			} );
		}
	};

	/**
	 * Checks if all options have been selected from the list of settings.
	 *
	 * @return {boolean} true if all it set.
	 */
	areAllOptionsSelected = () => {
		return Object.keys( this.settings ).every(
			settingId => {
				return this.selectedOptions[ settingId ] !== undefined && this.selectedOptions[ settingId ] !== '';
			}
		);
	};

	/**
	 * Loop through and reset all form fields since no field should now be selected.
	 * that basically means no field options should be disabled and min/max be restored.
	 *
	 * We also:
	 * * remove the price.
	 * * disable add to cart.
	 *
	 */
	resetFormFields = () => {
		// Remove price and disable add to cart button.
		this.removePrice();
		this.disableAddToCart( true );
		// Also empty out selected options.
		this.selectedOptions = {};

		Object.keys( this.settings ).forEach( settingId => {
			const formElement = document.querySelector( this.inputTarget + '--' + settingId );
			if ( ! formElement ) {
				return;
			}
			// Reset value to nothing.
			formElement.value = '';

			const inputType = formElement.tagName.toLowerCase();
			const inputSubType = formElement.type ?? '';
			switch ( inputType ) {
				case 'select':
					this.resetSelectField( formElement );
					break;
				case 'input':
					if ( inputSubType === 'number' ) {
						this.resetNumberField( formElement );
					} else {
						this.resetInputField( formElement );
					}
					break;
				default:
			}
		} );
	};

	/**
	 * Perform necessary logic to reset a select field.
	 *
	 * @param {HTMLElement} formElement The select field.
	 */
	resetSelectField = formElement => {
		for ( let i = formElement.options.length - 1; i > 0; i-- ) {
			formElement.options[ i ].disabled = false;
		}
	};

	/**
	 * Perform necessary logic to reset a number input field.
	 *
	 * @param {HTMLElement} formElement The number input field.
	 */
	resetNumberField = formElement => {
		// Check if we have stored original min/max
		const originalMin = formElement.dataset.min ?? formElement.min;
		const originalMax = formElement.dataset.max ?? formElement.max;

		// reset max min values.
		formElement.min = originalMin;
		formElement.max = originalMax;

		// Set description info if that exists.
		const minInfoElement = formElement
			.closest( '.qdp__pdp-field' )
			.querySelector( '.qdp__field-min' );
		const maxInfoElement = formElement
			.closest( '.qdp__pdp-field' )
			.querySelector( '.qdp__field-max' );
		if ( minInfoElement ) {
			minInfoElement.textContent = originalMin;
		}
		if ( maxInfoElement ) {
			maxInfoElement.textContent = originalMax;
		}
	};

	/**
	 * Performs necessary logic to reset a text input.
	 *
	 * @param {HTMLElement} formElement The text input.
	 */
	resetInputField = formElement => { // eslint-disable-line
		// Logic for formElement..
	};

	/**
	 * Updates the price with the price data to match the options we have selected.
	 *
	 * @param {string} price the new price.
	 * @return {void}
	 */
	updatePrice = ( price = '' ) => {
		const priceElement = document.querySelector( '.qdp__price' );
		if ( ! priceElement ) {
			return;
		}

		// Hide price if we don't have any. But we still allow for 0 price.
		if ( typeof price === 'undefined' || price === null || price === '' ) {
			priceElement.classList.remove( 'qdp__price--show' );
		}

		priceElement.innerHTML = price;
		priceElement.classList.add( 'qdp__price--show' );
	};

	/**
	 * Updates the select options with just the ones we have now.
	 *
	 * TODO: We have removed the disabling of options for the select fields for now.
	 * This doesn't work well since its a many to many relationship we are trying to validate.
	 * We would need to change to a top->down approach where you edit one field at a time.
	 *
	 * @param {HTMLElement} formElement Optional. pass the form element directly.
	 * @param {Array}       options     The options to set new configuration from.
	 * @param {string}      settingId   Optional. Pass a setting ID if you don't have a form element and we will attempt to get it.
	 */
	updateSelectField = ( formElement = null, options, settingId = null ) => {
		// If we don't have a formElement but we do have a setting ID lets try to get it.
		if ( formElement === null && settingId !== null ) {
			formElement = document.querySelector( this.inputTarget + '--' + settingId );
		}

		if ( ! formElement ) {
			return; // eslint-disable-line
		}

		/*
		// Because select options values are always strings, we need to make sure our new options are too for comparison.
		options = options.map( item => String( item ) );

		// Loop through and disable options that aren't in our returned options.
		for ( let i = formElement.options.length - 1; i > 0; i-- ) {
			const currentValue = formElement.options[ i ].value;
			// Disable the option if its not the placeholder option or not in our returned options.
			const isDisabled =
				currentValue !== '' && options.includes( currentValue ) ? false : true;
			formElement.options[ i ].disabled = isDisabled;

			// If the selected value is a now disabled option we also want to clear the select.
			if ( isDisabled === true && currentValue === formElement?.value ) {
				formElement.value = '';
				// incorrect form value means we need to disable button and remove price.
				this.disableAddToCart( true );
				this.removePrice();
			}
		}
		*/
	};

	/**
	 * Updates the input based on its type.
	 *
	 * @param {HTMLElement} formElement Optional. pass the form element directly.
	 * @param {Array}       options     The options to set new configuration from.
	 * @param {string}      settingId   Optional. Pass a setting ID if you don't have a form element and we will attempt to get it.
	 */
	updateInputField = ( formElement = null, options, settingId = null ) => {
		// If we don't have a formElement but we do have a setting ID lets try to get it.
		if ( formElement === null && settingId !== null ) {
			formElement = document.querySelector( this.inputTarget + '--' + settingId );
		}

		// Run different updates depending on the input type.
		switch ( formElement.type ) {
			case 'number':
				this.updateNumberField( formElement, options );
				break;
			default:
				this.updateTextField( formElement, options );
		}
	};

	/**
	 * Updates the numbers inputs min/max value restrictions based on current selections availability etc.
	 *
	 * @param {HTMLElement} formElement Optional. pass the form element directly.
	 * @param {Array}       options     The options to set new configuration from.
	 */
	updateNumberField = ( formElement, options ) => {
		if ( ! formElement ) {
			return;
		}
		// Lets make sure all options are numbers first.
		const numberOptions = options.map( Number );

		// Get the max/min values.
		const newMax = Math.max( ...numberOptions ); // eslint-disable-line
		const newMin = Math.min( ...numberOptions ); // eslint-disable-line

		// TODO: Note that this won't really work since we are presently getting back just the number we put in.
		// We can probably return this functionality once the response values back from the server are more accurate.
		/*
		if ( formElement.value > newMax || formElement.value < newMin ) {
			// incorrect form value means we need to disable button and remove price.
			this.disableAddToCart( true );
			this.removePrice();
		}
		*/

		// If there is already a value in the number input we do not want to change the min/max values.
		// Since that will be weird because it will always just set both min/max to the value of the closest valid value from the matrix.
		if ( formElement.value !== '' ) {
			return;
		}

		// Store original min/max values as data.
		formElement.dataset.min = formElement.min;
		formElement.dataset.max = formElement.max;

		// Set new max min values.
		formElement.min = newMin;
		formElement.max = newMax;

		// Set description info if that exists.
		const minInfoElement = formElement
			.closest( '.qdp__pdp-field' )
			.querySelector( '.qdp__field-min' );
		const maxInfoElement = formElement
			.closest( '.qdp__pdp-field' )
			.querySelector( '.qdp__field-max' );
		if ( minInfoElement ) {
			minInfoElement.textContent = newMin;
		}
		if ( maxInfoElement ) {
			maxInfoElement.textContent = newMax;
		}
	};

	/**
	 * Updates the text input as needed.
	 * Note: right now there is no update happening on a regular text input.
	 *
	 * @param {HTMLElement} formElement Optional. pass the form element directly.
	 * @param {Array}       options     The options to set new configuration from.
	 */
	updateTextField = ( formElement, options ) => { // eslint-disable-line
		// eslint-disable-line
		if ( ! formElement ) {
			return; // eslint-disable-line
		}

		// … logic goes here.
	};
}
